import * as React from 'react';
import { Spinner, SpinnerSize, TextField, DefaultButton, Link, Dropdown } from 'office-ui-fabric-react';
import { connect } from 'react-redux';
import { ApplicationState, SignUpPlan, RegionInfo } from '../../store';
import { TenantState, Tenant, actionCreators, ProductType } from '../../store/Tenant';
import { UserState } from '../../store/User';
import { Validator } from '../../validation';
import { RouteComponentProps, withRouter } from "react-router-dom";
import { getCookie, inIframe } from '../utils/common';
import { SignUpPlans } from './SignUpPlans'
import { SignUpParams } from '../../LoginPage';
import { DateTime } from 'luxon';

const getCurrentBrowserLocale = () => window.navigator.language || (window.navigator as any).userLanguage;

type OwnProps = {};
type StateProps = {
    user: UserState;
    tenant: TenantState;
    plans: SignUpPlan[];
    region: string | null;
    regions: RegionInfo[];
};
type Props = StateProps & typeof actionCreators & OwnProps & RouteComponentProps<{}>;

type TenantSetupState = {
    tenant: Tenant;
    selectedProductType?: ProductType;
    disableReferralCode: boolean;
    isLocked: boolean;
}

const validator: { [key: string]: Validator } = {
    companyName: Validator.new().required().noPictographicCharacters().maxLength(255).build(),
    companyWebSite: Validator.new().url().build(),
    personName: Validator.new().required().build(),
    personEmail: Validator.new().required().email().build(),
    personPhoneNumber: Validator.new().required().phone().build(),
}

function initState(props: Props): TenantSetupState {
    const queryParams = new URLSearchParams(props.location.search);
    const planKey = (queryParams.get(SignUpParams.Plan) ?? getCookie(SignUpParams.Plan))?.toLowerCase();
    const isLocked = (queryParams.get(SignUpParams.Type) ?? getCookie(SignUpParams.Type)) === 'locked';
    const plan = props.location.state?.source?.toLowerCase() === '/teamsapp/config' && props.plans.find(_ => _.isDefaultFromTeams)
        || props.plans.find(_ => _.key?.toLowerCase() === planKey)
        || props.plans.find(_ => _.isDefault);

    const tenant = new Tenant();
    tenant.companyName = queryParams.get(SignUpParams.CompanyName) || props.tenant.name;
    tenant.personName = queryParams.get(SignUpParams.PersonName) || props.user.name;
    tenant.personEmail = queryParams.get(SignUpParams.PersonEmail) || props.user.email;
    tenant.timezone = DateTime.local().zoneName;
    tenant.plan = plan?.key || '';
    tenant.referralCode = queryParams.get(SignUpParams.ReferralCode) || getCookie(SignUpParams.ReferralCode) || '';
    tenant.companyName = queryParams.get(SignUpParams.CompanyName) || getCookie(SignUpParams.CompanyName) || '';
    tenant.personPhoneNumber = queryParams.get(SignUpParams.PersonPhone) || getCookie(SignUpParams.PersonPhone) || '';
    tenant.cultureName = getCurrentBrowserLocale();

    return {
        tenant,
        selectedProductType: plan?.productType || ProductType.Product,
        disableReferralCode: !!tenant.referralCode && !queryParams.get(SignUpParams.ReferralCode),
        isLocked: !!plan && isLocked,
    };
}

const TenantSetup = (props: Props) => {
    const [state, setState] = React.useState<TenantSetupState>(() => initState(props));

    if (props.tenant.id) {
        return null;
    }

    const { tenant, selectedProductType, disableReferralCode, isLocked } = state;

    const updateTenant = (patch: Partial<Tenant>) =>
        setState(prev => ({ ...prev, tenant: { ...prev.tenant, ...patch } as Tenant }));

    const isFormValid = () => Validator.isValid(validator, tenant);

    const redirectToRegion = (newRegion: string) => {
        const params = new URLSearchParams();
        if (tenant.companyName) {
            params.set(SignUpParams.CompanyName, tenant.companyName);
        }
        if (tenant.personName) {
            params.set(SignUpParams.PersonName, tenant.personName);
        }
        if (tenant.personEmail) {
            params.set(SignUpParams.PersonEmail, tenant.personEmail);
        }
        if (tenant.personPhoneNumber) {
            params.set(SignUpParams.PersonPhone, tenant.personPhoneNumber);
        }
        if (tenant.referralCode) {
            params.set(SignUpParams.ReferralCode, tenant.referralCode);
        }

        const currentQueryParams = new URLSearchParams(props.location.search);
        const type = currentQueryParams.get(SignUpParams.Type);
        if (type) {
            params.set(SignUpParams.Type, type);
        }
        const planKey = currentQueryParams.get(SignUpParams.Plan);
        if (planKey) {
            params.set(SignUpParams.Plan, planKey);
        }

        const qs = params.toString();
        window.location.href = `/${newRegion}/setup/tenant${qs ? '?' + qs : ''}`;
    };

    const setSelectedPlan = (plan: SignUpPlan) =>
        setState(prev => ({
            ...prev,
            selectedProductType: plan.productType,
            tenant: { ...prev.tenant, plan: plan.key },
        }));

    const registerTenant = () => {
        if (isFormValid()) {
            props.registerTenant(tenant);
        }
    };

    return (
        <div className='tenant-setup'>
            <div className='profile'>
                <div className={`info ${inIframe() ? 'info-wide' : ''}`}>
                    <h1>Registration</h1>
                    <div className="tenant-setup-description">Create your account and get a 30-day Trial for FREE. No credit card required.</div>
                    {props.regions.length > 1 && <Dropdown
                        label='Data Center Region'
                        selectedKey={props.region}
                        options={props.regions.map(r => ({ key: r.region, text: r.title }))}
                        onChange={(e, option) => option && redirectToRegion(option.key as string)} />}
                    <TextField
                        label='Company'
                        placeholder='Company Name'
                        required
                        value={tenant.companyName}
                        onGetErrorMessage={validator.companyName.getErrorMessage}
                        onChange={(_e, value) => updateTenant({ companyName: value! })} />
                    <TextField
                        label='Website'
                        inputMode='url'
                        placeholder='company.com'
                        onGetErrorMessage={validator.companyWebSite.getErrorMessage}
                        onChange={(_e, value) => updateTenant({ companyWebSite: value! })} />
                    <TextField
                        label='Job Title'
                        placeholder='Position'
                        onChange={(_e, value) => updateTenant({ personJobTitle: value! })} />
                    <TextField
                        label='Name'
                        placeholder='Full Name'
                        required
                        value={tenant.personName}
                        onGetErrorMessage={validator.personName.getErrorMessage}
                        onChange={(_e, value) => updateTenant({ personName: value! })} />
                    <TextField
                        label='Email'
                        inputMode='email'
                        placeholder='Email Address'
                        required
                        value={tenant.personEmail}
                        onGetErrorMessage={validator.personEmail.getErrorMessage}
                        onChange={(_e, value) => updateTenant({ personEmail: value! })} />
                    <TextField
                        label='Phone Number'
                        inputMode='tel'
                        placeholder='+1 (999)-999-9999'
                        required
                        value={tenant.personPhoneNumber}
                        onGetErrorMessage={validator.personPhoneNumber.getErrorMessage}
                        onChange={(_e, value) => updateTenant({ personPhoneNumber: value! })} />
                    <TextField
                        label='Referral Code (if you have it)'
                        placeholder='Enter code'
                        maxLength={99}
                        disabled={disableReferralCode}
                        value={tenant.referralCode}
                        onChange={(_e, value) => updateTenant({ referralCode: value! })} />
                    <div className='register'>
                        <DefaultButton
                            disabled={props.tenant.isProcessing || !isFormValid()}
                            primary
                            text={props.tenant.isProcessing ? 'Registration' : 'Register'}
                            onClick={registerTenant}>
                            {props.tenant.isProcessing && <Spinner size={SpinnerSize.medium} />}
                        </DefaultButton>
                    </div>
                    <p className='signup-agreement'>
                        By registering, you agree to our <Link href='https://ppm.express/terms-of-use' target='_blank'>Terms of Use</Link> &nbsp;
                        and <Link href='https://ppm.express/privacy-policy' target='_blank'>Privacy</Link>
                    </p>
                    <p className='pricing-info'>
                        <Link href='https://ppm.express/ppm-express-pricing' target='_blank'>Pricing and Plans</Link>
                    </p>
                </div>
            </div>
            <div className='wallpaper-container'>
                {!!props.plans.length && <SignUpPlans
                    availablePlans={props.plans}
                    selectedProductType={selectedProductType}
                    onPlanClick={productType => {
                        const plan = props.plans.find(_ => _.productType === productType);
                        plan && setSelectedPlan(plan);
                    }}
                    isLocked={isLocked}
                />}
                <div className='wallpaper'></div>
            </div>
        </div>
    );
};

function mapStateToProps(state: ApplicationState): StateProps {
    return {
        user: state.user,
        tenant: state.tenant,
        plans: state.chargebee.plans,
        region: state.tenant.region,
        regions: state.enviroment.regions,
    };
}

export default withRouter<OwnProps>(connect(mapStateToProps, actionCreators)(TenantSetup));
